import React, { useEffect, useState } from 'react';
import { ArrowRight, ArrowUpRight, Mail, Menu, X } from 'lucide-react';

const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
};

const products = [
  {
    name: '초간편고객관리',
    english: 'SoEasyCRM',
    category: 'CRM',
    status: 'Released',
    description: '고객 정보와 소통 기록에만 집중한 초간단 고객관리 앱.',
    url: 'https://play.google.com/store/apps/details?id=com.previaworks.soeasycrm',
    destination: 'google_play',
    accent: '#5b5bd6',
    surface: '#f3f3ff',
    initial: 'S'
  },
  {
    name: '실시간 암산왕',
    english: 'Mental Math',
    category: 'Game',
    status: 'Released',
    description: '틀릴 때까지 이어지는 연속 암산과 기록 경쟁.',
    url: 'https://play.google.com/store/apps/details?id=com.previaworks.mathcombo',
    destination: 'google_play',
    accent: '#2563eb',
    surface: '#edf4ff',
    initial: 'M'
  },
  {
    name: '니혼고킹',
    english: 'NihongoKing',
    category: 'Learning',
    status: 'Building',
    description: '듣고 넘기고 다시 푸는 흐름의 일본어 학습 앱.',
    accent: '#c27a00',
    surface: '#fff7e6',
    initial: 'N'
  },
  {
    name: '크로카',
    english: 'Croca',
    category: 'Game',
    status: 'Building',
    description: 'Godot으로 만드는 모바일 플랫폼 게임.',
    accent: '#1f8a55',
    surface: '#edf9f2',
    initial: 'C'
  },
  {
    name: '설도프',
    english: 'Seoldorf',
    category: 'Content',
    status: 'Live',
    description: '보험설계사를 위한 실전 콘텐츠와 매뉴얼 플랫폼.',
    url: 'https://설도프.com',
    destination: 'website',
    accent: '#0f766e',
    surface: '#ecfbf8',
    initial: 'D'
  }
];

const updates = [
  {
    date: 'SEP 2026',
    title: 'PreviaWorks의 방향을 Independent Product Studio로 전환',
    body: '한 가지 서비스보다 여러 작은 제품을 직접 만들고 출시하는 스튜디오로 브랜드를 다시 정리했습니다.'
  },
  {
    date: 'SEP 2026',
    title: 'React Native와 Godot을 함께 사용',
    body: '앱과 게임의 성격에 따라 도구를 나누고, 빠르게 실기기에서 검증하는 방식을 유지하고 있습니다.'
  },
  {
    date: 'SEP 2026',
    title: '초간편고객관리 2.0 출시',
    body: '고객 저장과 소통 기록이라는 핵심 사용 흐름에 집중해서 제품을 다듬었습니다.'
  }
];

function ProductCard({ product }) {
  const Card = product.url ? 'a' : 'article';

  return (
    <Card
      {...(product.url
        ? {
            href: product.url,
            target: '_blank',
            rel: 'noopener noreferrer',
            onClick: () =>
              trackEvent('product_click', {
                product: product.english,
                destination: product.destination || 'google_play'
              })
          }
        : {})}
      className="group flex h-full flex-col rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-slate-300 hover:shadow-xl hover:shadow-slate-200/50"
    >
      <div className="flex items-start justify-between gap-4">
        <div
          className="grid h-14 w-14 place-items-center rounded-2xl text-lg font-black"
          style={{ backgroundColor: product.surface, color: product.accent }}
        >
          {product.initial}
        </div>

        <span className="rounded-full bg-slate-100 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500">
          {product.status}
        </span>
      </div>

      <div className="mt-8">
        <p className="text-xs font-bold uppercase tracking-[0.15em]" style={{ color: product.accent }}>
          {product.category}
        </p>
        <h3 className="mt-2 text-2xl font-bold tracking-[-0.04em] text-slate-950">{product.name}</h3>
        <p className="mt-1 text-sm font-medium text-slate-400">{product.english}</p>
        <p className="mt-5 text-[15px] leading-7 text-slate-600">{product.description}</p>
      </div>

      <div className="mt-auto flex items-center justify-between pt-10 text-sm font-semibold text-slate-700">
        <span>
          {product.url
            ? product.destination === 'website'
              ? 'Visit website'
              : 'Google Play'
            : 'In development'}
        </span>
        {product.url && (
          <ArrowUpRight
            size={17}
            className="transition duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          />
        )}
      </div>
    </Card>
  );
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    document.body.classList.add('bright-product-body');
    return () => document.body.classList.remove('bright-product-body');
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const copyEmail = async () => {
    const email = 'hello@previaworks.com';

    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      trackEvent('email_copy', { location: 'contact' });
      setTimeout(() => setCopied(false), 1600);
    } catch {
      window.location.href = `mailto:${email}`;
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f8fa] text-slate-950">
      <header className="sticky top-0 z-50 border-b border-slate-200/80 bg-[#f7f8fa]/92 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:px-8">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-3"
            aria-label="맨 위로 이동"
          >
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-[#265df5] text-sm font-black text-white shadow-sm">
              P
            </span>
            <span className="text-sm font-extrabold tracking-[-0.025em]">PREVIAWORKS</span>
          </button>

          <nav className="hidden items-center gap-8 text-sm font-semibold text-slate-500 md:flex">
            <button onClick={() => scrollTo('products')} className="transition hover:text-slate-950">Products</button>
            <button onClick={() => scrollTo('updates')} className="transition hover:text-slate-950">Updates</button>
            <button onClick={() => scrollTo('about')} className="transition hover:text-slate-950">About</button>
            <button
              onClick={() => scrollTo('contact')}
              className="rounded-xl bg-slate-950 px-4 py-2.5 text-white transition hover:bg-slate-800"
            >
              Contact
            </button>
          </nav>

          <button
            className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white md:hidden"
            onClick={() => setMenuOpen((value) => !value)}
            aria-label="메뉴"
          >
            {menuOpen ? <X size={21} /> : <Menu size={21} />}
          </button>
        </div>

        {menuOpen && (
          <div className="border-t border-slate-200 bg-white px-5 py-5 md:hidden">
            <div className="flex flex-col gap-4 text-left text-base font-semibold text-slate-700">
              <button onClick={() => scrollTo('products')} className="text-left">Products</button>
              <button onClick={() => scrollTo('updates')} className="text-left">Updates</button>
              <button onClick={() => scrollTo('about')} className="text-left">About</button>
              <button onClick={() => scrollTo('contact')} className="text-left">Contact</button>
            </div>
          </div>
        )}
      </header>

      <main>
        <section className="relative overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-[620px] bg-[radial-gradient(circle_at_82%_18%,rgba(59,130,246,0.12),transparent_34%),radial-gradient(circle_at_18%_0%,rgba(139,92,246,0.08),transparent_30%)]" />

          <div className="relative mx-auto max-w-7xl px-5 pb-24 pt-20 md:px-8 md:pb-32 md:pt-28">
            <div className="grid items-center gap-14 lg:grid-cols-[1.15fr_0.85fr]">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-500 shadow-sm">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  Independent Product Studio
                </div>

                <h1 className="mt-7 max-w-4xl text-5xl font-extrabold leading-[1.02] tracking-[-0.055em] text-slate-950 sm:text-6xl md:text-7xl">
                  Small products.
                  <br />
                  Real usefulness.
                </h1>

                <p className="mt-7 max-w-2xl text-lg leading-8 text-slate-600 md:text-xl">
                  PreviaWorks는 Woodie Choi가 직접 만들고 출시하는 작은 제품들의 스튜디오입니다.
                  앱, 게임, 웹서비스를 빠르게 만들고 실제 사용으로 검증합니다.
                </p>

                <div className="mt-9 flex flex-wrap gap-3">
                  <button
                    onClick={() => scrollTo('products')}
                    className="inline-flex items-center gap-2 rounded-xl bg-[#265df5] px-5 py-3.5 text-sm font-bold text-white shadow-lg shadow-blue-200/50 transition hover:bg-[#1f4fd9]"
                  >
                    Explore products <ArrowRight size={17} />
                  </button>
                  <button
                    onClick={() => scrollTo('about')}
                    className="rounded-xl border border-slate-200 bg-white px-5 py-3.5 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50"
                  >
                    About Woodie
                  </button>
                </div>
              </div>

              <div className="rounded-[32px] border border-slate-200 bg-white p-5 shadow-2xl shadow-slate-200/70">
                <div className="rounded-[24px] bg-[#f1f5ff] p-6 md:p-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#265df5]">Currently building</p>
                      <h2 className="mt-2 text-2xl font-extrabold tracking-[-0.04em] text-slate-950">Products in motion</h2>
                    </div>
                    <span className="rounded-full bg-white px-3 py-1.5 text-xs font-bold text-slate-500">SEP 2026</span>
                  </div>

                  <div className="mt-7 space-y-3">
                    {products.slice(0, 4).map((product) => (
                      <div key={product.english} className="flex items-center justify-between rounded-2xl bg-white p-4 shadow-sm">
                        <div className="flex items-center gap-3">
                          <div
                            className="grid h-10 w-10 place-items-center rounded-xl text-sm font-black"
                            style={{ backgroundColor: product.surface, color: product.accent }}
                          >
                            {product.initial}
                          </div>
                          <div>
                            <p className="text-sm font-bold text-slate-900">{product.name}</p>
                            <p className="mt-0.5 text-xs text-slate-400">{product.category}</p>
                          </div>
                        </div>
                        <span className="text-xs font-bold text-slate-400">{product.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-16 grid gap-4 border-t border-slate-200 pt-7 text-sm sm:grid-cols-3">
              <div>
                <p className="font-bold text-slate-900">Focus</p>
                <p className="mt-1 text-slate-500">Apps · Games · Web</p>
              </div>
              <div>
                <p className="font-bold text-slate-900">Based in</p>
                <p className="mt-1 text-slate-500">Korea</p>
              </div>
              <div>
                <p className="font-bold text-slate-900">Since</p>
                <p className="mt-1 text-slate-500">2025</p>
              </div>
            </div>
          </div>
        </section>

        <section id="products" className="scroll-mt-20 border-t border-slate-200 bg-white">
          <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
            <div className="mb-12 grid gap-5 md:grid-cols-[1fr_auto] md:items-end">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#265df5]">Products</p>
                <h2 className="mt-3 text-4xl font-extrabold tracking-[-0.045em] text-slate-950 md:text-5xl">
                  Built to be used.
                </h2>
              </div>
              <p className="max-w-md text-sm leading-6 text-slate-500">
                거대한 계획보다 실제로 만들어지고, 실행되고, 개선되는 제품을 보여줍니다.
              </p>
            </div>

            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {products.map((product) => (
                <ProductCard key={product.english} product={product} />
              ))}

              <div className="flex min-h-[330px] flex-col justify-between rounded-[28px] border border-dashed border-slate-300 bg-slate-50 p-6">
                <div>
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Next</span>
                  <h3 className="mt-3 text-3xl font-extrabold tracking-[-0.045em] text-slate-900">
                    More small products are coming.
                  </h3>
                </div>
                <p className="text-sm leading-6 text-slate-500">
                  완벽해질 때까지 기다리기보다 작게 만들고 빠르게 출시합니다.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="updates" className="scroll-mt-20 border-t border-slate-200 bg-[#f7f8fa]">
          <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
            <div className="grid gap-12 lg:grid-cols-[0.7fr_1.3fr]">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#265df5]">Updates</p>
                <h2 className="mt-3 text-4xl font-extrabold tracking-[-0.045em] text-slate-950 md:text-5xl">
                  What’s moving now.
                </h2>
                <p className="mt-5 max-w-sm text-sm leading-6 text-slate-500">
                  완성품뿐 아니라 지금 어떤 방향으로 움직이고 있는지도 기록합니다.
                </p>
              </div>

              <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm">
                {updates.map((update, index) => (
                  <article
                    key={update.title}
                    className={`grid gap-4 p-6 md:grid-cols-[130px_1fr] md:p-7 ${index !== updates.length - 1 ? 'border-b border-slate-200' : ''}`}
                  >
                    <p className="text-xs font-bold tracking-[0.12em] text-slate-400">{update.date}</p>
                    <div>
                      <h3 className="text-lg font-bold tracking-[-0.025em] text-slate-900">{update.title}</h3>
                      <p className="mt-2 text-sm leading-6 text-slate-500">{update.body}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="about" className="scroll-mt-20 border-t border-slate-200 bg-white">
          <div className="mx-auto grid max-w-7xl gap-14 px-5 py-20 md:px-8 md:py-28 lg:grid-cols-[0.8fr_1.2fr]">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#265df5]">About</p>
              <h2 className="mt-3 text-4xl font-extrabold tracking-[-0.045em] text-slate-950 md:text-5xl">
                Woodie Choi
              </h2>
              <p className="mt-3 text-sm font-semibold text-slate-400">Indie Maker · Product Builder</p>
            </div>

            <div className="max-w-2xl">
              <p className="text-2xl font-bold leading-9 tracking-[-0.03em] text-slate-900 md:text-3xl">
                16년 동안 세일즈 현장에서 일했고,
                지금은 작은 제품을 직접 기획하고 만들어 출시합니다.
              </p>

              <div className="mt-7 space-y-4 text-[15px] leading-7 text-slate-600">
                <p>
                  반복해서 마주치는 불편을 발견하면 직접 해결해 봅니다.
                  React Native로 앱을 만들고, Godot으로 게임을 만들고,
                  웹에서는 콘텐츠 서비스를 운영합니다.
                </p>
                <p>
                  기술 자체보다 실제로 쓰이는지, 그리고 계속 개선할 수 있는지를 더 중요하게 봅니다.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-2">
                {['Indie Maker', 'Product Builder', 'React Native', 'Godot', 'Web'].map((item) => (
                  <span key={item} className="rounded-full bg-slate-100 px-3 py-2 text-xs font-bold text-slate-600">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="scroll-mt-20 border-t border-slate-200 bg-[#eef4ff]">
          <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-24">
            <div className="rounded-[32px] border border-blue-100 bg-white p-7 shadow-xl shadow-blue-100/60 md:p-10 lg:p-12">
              <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#265df5]">Contact</p>
                  <h2 className="mt-3 text-4xl font-extrabold tracking-[-0.045em] text-slate-950 md:text-5xl">
                    Have an idea?
                  </h2>
                  <p className="mt-4 max-w-xl text-sm leading-6 text-slate-500">
                    제품, 협업, 재미있는 아이디어가 있다면 편하게 연락해 주세요.
                  </p>
                </div>

                <button
                  onClick={copyEmail}
                  className="inline-flex items-center gap-3 rounded-xl bg-[#265df5] px-5 py-3.5 text-sm font-bold text-white transition hover:bg-[#1f4fd9]"
                >
                  <Mail size={17} />
                  {copied ? 'Copied!' : 'hello@previaworks.com'}
                </button>
              </div>
            </div>

            <div className="mt-8 flex flex-col gap-2 text-xs font-medium text-slate-400 sm:flex-row sm:items-center sm:justify-between">
              <span>PREVIAWORKS · Independent Product Studio</span>
              <span>© {new Date().getFullYear()} Woodie Choi</span>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default App;
